import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useOnboardingStatus } from '../../hooks/api/use-onboarding.js';
import {
  isMandatoryOnboardingPath,
  MANDATORY_ONBOARDING_PATH,
  pendingMandatorySteps,
} from '../../onboarding/mandatory.js';
import { useOnboardingScopeState } from '../../onboarding/use-onboarding-scope.js';
import { SectionError, SectionLoading } from '../common/SectionState.js';

/**
 * Tant qu'une étape obligatoire reste à faire par ce membre, le reste du cabinet est fermé.
 * Les écrans des étapes obligatoires et facultatives restent ouverts. Une liste déjà masquée
 * (`checklistHidden`) ne ramène plus ici. Le serveur a calculé « fait » ; ici on ne fait que conduire.
 */
export function MandatoryOnboardingGate({ children }: { children: ReactNode }) {
  const [location, navigate] = useLocation();
  const scope = useOnboardingScopeState();
  const status = useOnboardingStatus(scope === 'in');
  const { refetch } = status;

  useEffect(() => {
    if (scope !== 'in') return;
    void refetch();
  }, [location, scope, refetch]);

  const blocking =
    scope === 'in' &&
    status.data !== undefined &&
    !status.data.checklistHidden &&
    pendingMandatorySteps(status.data.steps).length > 0;
  const allowed = isMandatoryOnboardingPath(location);

  useEffect(() => {
    if (blocking && !allowed) navigate(MANDATORY_ONBOARDING_PATH, { replace: true });
  }, [blocking, allowed, navigate]);

  if (scope === 'pending' || (scope === 'in' && status.isPending)) {
    return <SectionLoading />;
  }
  if (scope === 'in' && status.isError && status.data === undefined) {
    return <SectionError error={status.error} onRetry={() => void refetch()} />;
  }
  if (blocking && !allowed) return <SectionLoading />;
  return children;
}
